import _ from 'lodash';
import { FormatUtility } from '@codequality/vue-cli-base';

import { Eフェーズ種別 } from '@/openapi/enum';
import { データListItemResponse, Cデータ金融機関借入 } from '@/openapi/user';

import AbstractデータService, { ServiceType, 帳票データ } from '@/classes/service/AbstractデータService';
import AppFormatUtility from '@/classes/common/AppFormatUtility';

export type 金融機関借入ListItem = Cデータ金融機関借入 & {
  データpk?: number;
  番号?: number;
  フェーズ種別?: Eフェーズ種別;
};

type 残高 = Required<Pick<Cデータ金融機関借入, '前期末残高' | '直近月残高'>> & {
  割合?: string;
};

type 項目別集計 = 残高 & {
  行番号: number;
  集計項目: string;
  副集計項目?: string;
};

export type 金融機関借入 = {
  list: 金融機関借入ListItem[];
  total: {
    all: 残高;
    金融機関別: 項目別集計[];
    科目別: 項目別集計[];
  };
};

export default class 金融機関借入Service extends AbstractデータService<金融機関借入> {
  protected serviceType = ServiceType.金融機関借入;

  /**
   * @override
   * @param データs
   * @returns
   */
  public parse(データs: データListItemResponse[]): 金融機関借入 {
    // 必要データに絞り込んで計算可能データ化
    const filteredデータs = this.入力分類Filter(データs);

    // 一覧
    const list = filteredデータs.map((d) => {
      return {
        データpk: d.pk,
        番号: d.c番号,
        フェーズ種別: d.cフェーズ種別,
        ...(JSON.parse(d.cデータ) as Cデータ金融機関借入),
      };
    });

    // 合計
    // 引数のListItemsが持つ前期末残高・直近月残高を合計し、残高型のオブジェクトを返す関数
    const sum残高 = (listItems: 金融機関借入ListItem[]): 残高 => {
      const v残高 = {
        前期末残高: 0,
        直近月残高: 0,
      };
      listItems.forEach((listItem) => {
        v残高.前期末残高 += listItem.前期末残高 ?? 0;
        v残高.直近月残高 += listItem.直近月残高 ?? 0;
      });
      return v残高;
    };
    const list残高合計 = sum残高(list);

    // 金融機関別合計
    // 金融機関借入合計ListItem[]を"金融機関:科目"の文字列でグループ化し、グループごとの値を集計
    const 機関科目別集計s = Object.values(_.groupBy(list, (listItem) => `${listItem.金融機関}:${listItem.科目}`));
    const 金融機関別: 項目別集計[] = 機関科目別集計s.map((listItems, index) => {
      const v残高 = sum残高(listItems);
      const firstItem = listItems[0];
      return {
        行番号: index + 1,
        集計項目: firstItem.金融機関 ?? '',
        副集計項目: firstItem.科目,
        割合:
          list残高合計.直近月残高 === 0
            ? ''
            : FormatUtility.toFixed((v残高.直近月残高 / list残高合計.直近月残高) * 100, 1),
        ...v残高,
      };
    });

    // 科目別合計
    const 科目別集計s = Object.values(_.groupBy(list, '科目'));
    const 科目別: 項目別集計[] = 科目別集計s.map((listItems, index) => {
      return {
        行番号: index + 1,
        集計項目: listItems[0].科目,
        ...sum残高(listItems),
      };
    });

    return {
      list,
      total: {
        all: {
          ...list残高合計,
          割合: list残高合計.直近月残高 === 0 ? '' : '100.0%',
        },
        金融機関別,
        科目別,
      },
    };
  }

  /**
   * @override
   * @param データs
   * @returns
   */
  public parse帳票ListItemRequest(データs: データListItemResponse[]): 帳票データ {
    const raw = this.parse(データs);

    // テンプレート行設定
    const templateConfig = {
      list: {
        header: 0,
        row: {
          odd: 1,
          even: 2,
        },
        total: 3,
      },
      金融機関別: {
        row: {
          odd: 4,
          even: 5,
        },
        total: 6,
      },
      科目別: {
        row: {
          odd: 7,
          even: 8,
        },
        total: 9,
      },
    };

    // 以下帳票データ整形
    // 初行設定込みで初期化
    const templateRows: number[] = [templateConfig.list.header];

    const values: unknown[][] = [
      [
        ...this.templateCellValues(15),
        `直近月残高\n(${AppFormatUtility.formatYm(AbstractデータService.計画書.c実績直近月)})`,
        ...this.templateCellValues(3),
      ],
    ];

    // 残高合計の整形関数
    const push残高合計 = (v残高合計: 残高) => {
      values.push([
        ...this.templateCellValues(14),
        this.convertByMoneyUnit(v残高合計.前期末残高),
        this.convertByMoneyUnit(v残高合計.直近月残高),
        v残高合計.割合,
        ...this.templateCellValues(2),
      ]);
    };

    // 集計別項目の整形関数
    const push項目別集計 = (v集計: 項目別集計) => {
      values.push([
        v集計.行番号,
        v集計.集計項目,
        v集計.副集計項目,
        ...this.templateCellValues(11),
        this.convertByMoneyUnit(v集計.前期末残高),
        this.convertByMoneyUnit(v集計.直近月残高),
        FormatUtility.percent(v集計.割合),
        ...this.templateCellValues(2),
      ]);
    };
    // 一覧整形
    raw.list.forEach((listItem, i) => {
      this.pushAlternateTemplateRow(templateRows, i, templateConfig.list.row.odd, templateConfig.list.row.even);
      values.push([
        listItem.番号,
        listItem.金融機関,
        listItem.種別,
        listItem.科目,
        this.convertByMoneyUnit(listItem.借入金額),
        listItem.返済方法,
        listItem.実行日,
        listItem.返済期日,
        listItem.元本返済開始日,
        listItem.返済回数,
        this.convertByMoneyUnit(listItem.初回元本返済額),
        this.convertByMoneyUnit(listItem.二回目以降元本返済額),
        this.convertByMoneyUnit(listItem.最終回元本返済額),
        FormatUtility.percent(FormatUtility.toFixed(listItem.当初金利, 3)),
        this.convertByMoneyUnit(listItem.前期末残高),
        this.convertByMoneyUnit(listItem.直近月残高),
        listItem.信用保証協会付,
        listItem.保証人,
        listItem.備考,
      ]);
    });

    // 合計整形
    templateRows.push(templateConfig.list.total);
    const { 割合, ...list残高合計 } = raw.total.all;
    push残高合計(list残高合計);

    //金融機関別整形
    raw.total.金融機関別.forEach((listItem, i) => {
      this.pushAlternateTemplateRow(
        templateRows,
        i,
        templateConfig.金融機関別.row.odd,
        templateConfig.金融機関別.row.even
      );
      push項目別集計({
        ...listItem,
      });
    });
    templateRows.push(templateConfig.金融機関別.total);
    push残高合計(raw.total.all);

    // 科目別整形
    raw.total.科目別.forEach((listItem, i) => {
      this.pushAlternateTemplateRow(templateRows, i, templateConfig.科目別.row.odd, templateConfig.科目別.row.even);
      push項目別集計({
        ...listItem,
      });
    });
    templateRows.push(templateConfig.科目別.total);
    push残高合計(list残高合計);

    return {
      templateRows,
      values,
    };
  }
}
